/**
 * Teacher → Dashboard (enhanced).
 *
 * Drives off the single /api/school/portal/teacher/dashboard payload —
 * no waterfall of N requests.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Users, BookOpen, Calendar, ClipboardList, AlertTriangle, MessageSquare,
  TrendingUp, Loader2, ChevronRight, CalendarRange, FileCheck,
} from 'lucide-react';
import { portalApi } from './teacherApi';

const KpiTile = ({ icon: Icon, label, value, tone = 'indigo', onClick }) => (
  <button onClick={onClick}
          className={`text-left bg-white border rounded-lg p-3 hover:shadow-sm transition ${onClick ? 'cursor-pointer hover:border-' + tone + '-300' : ''}`}>
    <div className={`inline-flex p-1.5 rounded bg-${tone}-100 text-${tone}-700`}>
      <Icon className="h-4 w-4" />
    </div>
    <div className="text-xl font-bold mt-2">{value ?? 0}</div>
    <div className="text-xs text-slate-500">{label}</div>
  </button>
);

const fmt = (d) => d ? new Date(d).toLocaleString() : '—';

export const TeacherDashboard = ({ onNavigate }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['teacher-dashboard'],
    queryFn: () => portalApi('/teacher/dashboard'),
    refetchInterval: 60_000,
  });
  const { data: weekly } = useQuery({
    queryKey: ['teacher-weekly'],
    queryFn: () => portalApi('/teacher/analytics/weekly'),
  });

  if (isLoading)
    return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /> Loading…</div>;
  if (!data)
    return <div className="p-8 text-center text-slate-500">Could not load dashboard.</div>;

  const { teacher, totals, today_schedule, tasks, stats, active_term } = data;
  const wc = weekly?.counts || {};
  const dueSoon = weekly?.due_soon || [];

  return (
    <div className="space-y-4" data-testid="teacher-dashboard-page">
      {/* Welcome */}
      <Card className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white border-0">
        <CardContent className="pt-5 pb-5">
          <h1 className="text-xl font-bold">Welcome back, {teacher.name || 'Teacher'} 👋</h1>
          <p className="text-sm opacity-90 mt-0.5">
            {active_term?.name ? `${active_term.name} · ` : ''}
            {totals.classes} {totals.classes === 1 ? 'class' : 'classes'} · {totals.subjects} {totals.subjects === 1 ? 'subject' : 'subjects'} · {totals.students} students
          </p>
        </CardContent>
      </Card>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
        <KpiTile icon={Users}         label="Students taught"  value={totals.students} onClick={() => onNavigate?.('students')} />
        <KpiTile icon={BookOpen}      label="Subjects"          value={totals.subjects} onClick={() => onNavigate?.('subjects')} />
        <KpiTile icon={CalendarRange} label="Classes"            value={totals.classes}  onClick={() => onNavigate?.('classes')} />
        <KpiTile icon={ClipboardList} label="Assignments made"   value={stats.assignments_created} onClick={() => onNavigate?.('assignments')} tone="emerald" />
        <KpiTile icon={FileCheck}     label="Exams owned"        value={stats.exams_created}        onClick={() => onNavigate?.('exams')} tone="violet" />
        <KpiTile icon={TrendingUp}    label="Avg attendance"    value={stats.average_attendance_pct != null ? `${stats.average_attendance_pct}%` : '—'} tone="sky" />
      </div>

      {/* Iter-411 — This-week productivity + due-soon rail */}
      {weekly && (
        <Card data-testid="weekly-analytics-card">
          <CardContent className="pt-4">
            <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-3">
              <TrendingUp className="h-4 w-4 text-indigo-500" /> This week
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
              <div className="rounded bg-indigo-50 py-2" data-testid="weekly-questions">
                <div className="text-lg font-bold text-indigo-700">{wc.questions_added || 0}</div>
                <div className="text-[11px] text-slate-500">Questions added</div>
              </div>
              <div className="rounded bg-emerald-50 py-2" data-testid="weekly-graded">
                <div className="text-lg font-bold text-emerald-700">{wc.submissions_graded || 0}</div>
                <div className="text-[11px] text-slate-500">Submissions graded</div>
              </div>
              <div className="rounded bg-amber-50 py-2" data-testid="weekly-assignments">
                <div className="text-lg font-bold text-amber-700">{wc.assignments_created || 0}</div>
                <div className="text-[11px] text-slate-500">Assignments created</div>
              </div>
              <div className="rounded bg-sky-50 py-2" data-testid="weekly-materials">
                <div className="text-lg font-bold text-sky-700">{wc.lms_materials_published || 0}</div>
                <div className="text-[11px] text-slate-500">Materials published</div>
              </div>
              <div className="rounded bg-violet-50 py-2" data-testid="weekly-ai">
                <div className="text-lg font-bold text-violet-700">{wc.ai_calls || 0}</div>
                <div className="text-[11px] text-slate-500">AI Copilot calls</div>
              </div>
            </div>
            {dueSoon.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs font-medium text-slate-600 mb-1.5">Due within 7 days</p>
                <ul className="space-y-1">
                  {dueSoon.map(a => (
                    <li key={a.id} className="text-xs flex justify-between items-center gap-2"
                        data-testid={`due-soon-${a.id}`}>
                      <span className="truncate">{a.title}</span>
                      <span className="text-slate-500 shrink-0">
                        {a.submission_count} submitted · due {new Date(a.due_at).toLocaleDateString()}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Today's schedule */}
        <Card>
          <CardContent className="pt-4">
            <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-2">
              <Calendar className="h-4 w-4 text-indigo-500" /> Today&apos;s schedule
            </h3>
            {today_schedule.length === 0 ? (
              <p className="text-sm text-slate-400">No classes scheduled today.</p>
            ) : (
              <div className="space-y-1.5">
                {today_schedule.map((slot, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b last:border-0 py-1.5">
                    <div>
                      <span className="font-medium">{slot.subject_name || slot.subject_code || 'Class'}</span>
                      <span className="text-slate-500"> · {slot.class_name || ''}</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      {slot.start_time}–{slot.end_time}
                      {slot.room && ` · ${slot.room}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tasks needing attention */}
        <Card>
          <CardContent className="pt-4">
            <h3 className="text-sm font-semibold mb-2">Needs your attention</h3>
            <div className="space-y-1.5">
              {tasks.pending_grading > 0 && (
                <button onClick={() => onNavigate?.('assignments')}
                        className="w-full flex items-center justify-between text-sm py-1.5 hover:bg-slate-50 rounded px-2 -mx-2">
                  <span className="flex items-center gap-2"><ClipboardList className="h-4 w-4 text-amber-600" />Assignments awaiting marking</span>
                  <Badge variant="secondary">{tasks.pending_grading}</Badge>
                </button>
              )}
              {tasks.pending_exams > 0 && (
                <button onClick={() => onNavigate?.('exams')}
                        className="w-full flex items-center justify-between text-sm py-1.5 hover:bg-slate-50 rounded px-2 -mx-2">
                  <span className="flex items-center gap-2"><FileCheck className="h-4 w-4 text-amber-600" />Exams in marking</span>
                  <Badge variant="secondary">{tasks.pending_exams}</Badge>
                </button>
              )}
              {tasks.at_risk > 0 && (
                <button onClick={() => onNavigate?.('students')}
                        className="w-full flex items-center justify-between text-sm py-1.5 hover:bg-slate-50 rounded px-2 -mx-2">
                  <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-rose-600" />Students flagged at risk</span>
                  <Badge variant="destructive">{tasks.at_risk}</Badge>
                </button>
              )}
              {tasks.unanswered_messages > 0 && (
                <div className="w-full flex items-center justify-between text-sm py-1.5">
                  <span className="flex items-center gap-2"><MessageSquare className="h-4 w-4 text-indigo-600" />Unanswered messages</span>
                  <Badge variant="secondary">{tasks.unanswered_messages}</Badge>
                </div>
              )}
              {tasks.pending_grading + tasks.pending_exams + tasks.at_risk + tasks.unanswered_messages === 0 && (
                <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                  🎉 You&apos;re all caught up — nothing pending.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming deadlines */}
      {tasks.upcoming_deadlines.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <h3 className="text-sm font-semibold mb-2">Upcoming deadlines · next 7 days</h3>
            <div className="space-y-1.5">
              {tasks.upcoming_deadlines.map((u) => (
                <div key={u.id} className="flex items-center justify-between text-sm border-b last:border-0 py-1.5">
                  <span>{u.title}</span>
                  <span className="text-xs text-amber-700">{fmt(u.due_date)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TeacherDashboard;
